import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const HRTrainingsPage = () => {
  const [trainings, setTrainings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [filter, setFilter] = useState({ status: '', training_type: '' });
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: '',
    training_type: 'internal',
    title: '',
    provider: '',
    start_date: '',
    end_date: '',
    duration_hours: '',
    cost: '',
    location: '',
    certification: false,
    notes: ''
  });
  const [editingTraining, setEditingTraining] = useState(null);
  const [bulkFormData, setBulkFormData] = useState({
    training_type: 'internal',
    title: '',
    description: '',
    provider: '',
    start_date: '',
    end_date: '',
    duration_hours: '',
    cost: '',
    location: '',
    certification: false,
    mandatory: false
  });
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchTrainings();
    fetchEmployees();
    if (isSuperAdmin) {
      fetchTenants();
    }
  }, [filter, selectedTenant]);

  const fetchTrainings = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.training_type) params.append('training_type', filter.training_type);
      
      const response = await api.get(`/hr/trainings?${params.toString()}`);
      setTrainings(response.data);
    } catch (error) {
      console.error('Error fetching trainings:', error);
      toast.error('Erro ao carregar formações');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      // Super admin must select tenant first to see employees
      if (isSuperAdmin && !selectedTenant) {
        setEmployees([]);
        return;
      }
      
      const params = new URLSearchParams();
      params.append('limit', '500');
      if (isSuperAdmin && selectedTenant) {
        params.append('tenant_id', selectedTenant);
      }
      const response = await api.get(`/employees?${params.toString()}`);
      setEmployees(response.data);
      setSelectedEmployees([]); // Reset selection when tenant changes
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (isSuperAdmin) {
        if (!selectedTenant) {
          toast.error('Selecione um tenant primeiro');
          return;
        }
        payload.tenant_id = selectedTenant;
      }
      await api.post('/hr/trainings', payload);
      toast.success('Formação criada com sucesso');
      setShowModal(false);
      fetchTrainings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar formação');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/hr/trainings/${editingTraining.training_id}`, formData);
      toast.success('Formação atualizada com sucesso');
      setEditingTraining(null);
      setShowModal(false);
      resetForm();
      fetchTrainings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar formação');
    }
  };

  const handleDelete = async (trainingId) => {
    if (!confirm('Tem certeza que deseja eliminar esta formação?')) return;
    try {
      await api.delete(`/hr/trainings/${trainingId}`);
      toast.success('Formação eliminada com sucesso');
      fetchTrainings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar formação');
    }
  };

  const openEditModal = (training) => {
    setEditingTraining(training);
    setFormData({
      employee_id: training.employee_id || '',
      training_type: training.training_type || 'internal',
      title: training.title || '',
      provider: training.provider || '',
      start_date: training.start_date || '',
      end_date: training.end_date || '',
      duration_hours: training.duration_hours || '',
      cost: training.cost || '',
      location: training.location || '',
      certification: training.certification || false,
      notes: training.notes || '',
      status: training.status || 'scheduled'
    });
    if (isSuperAdmin && training.tenant_id) {
      setSelectedTenant(training.tenant_id);
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      training_type: 'internal',
      title: '',
      provider: '',
      start_date: '',
      end_date: '',
      duration_hours: '',
      cost: '',
      location: '',
      certification: false,
      notes: ''
    });
    setEditingTraining(null);
    if (isSuperAdmin) setSelectedTenant('');
  };

  const handleBulkSchedule = async (e) => {
    e.preventDefault();
    if (selectedEmployees.length === 0) {
      toast.error('Selecione pelo menos um colaborador');
      return;
    }
    
    if (isSuperAdmin && !selectedTenant) {
      toast.error('Selecione um tenant primeiro');
      return;
    }
    
    setBulkLoading(true);
    try {
      const payload = {
        employee_ids: selectedEmployees,
        ...bulkFormData
      };
      if (isSuperAdmin) {
        payload.tenant_id = selectedTenant;
      }
      const response = await api.post('/hr/trainings/bulk-schedule', payload);
      toast.success(`Formação agendada para ${response.data.scheduled} colaboradores`);
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} erros encontrados`);
      }
      setShowBulkModal(false);
      setSelectedEmployees([]);
      setBulkFormData({
        training_type: 'internal', title: '', description: '', provider: '',
        start_date: '', end_date: '', duration_hours: '', cost: '',
        location: '', certification: false, mandatory: false
      });
      fetchTrainings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao agendar formação');
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleEmployee = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const selectAllEmployees = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(e => e.employee_id));
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    const labels = { scheduled: 'Agendada', in_progress: 'Em Curso', completed: 'Concluída', cancelled: 'Cancelada' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>{labels[status] || status}</span>;
  };

  const getTypeBadge = (type) => {
    const labels = { internal: 'Interna', external: 'Externa', online: 'Online', mandatory: 'Obrigatória' };
    return labels[type] || type;
  };

  const getEmployeeName = (id) => employees.find(e => e.employee_id === id)?.name || id;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="hr-trainings-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Formações</h1>
          <p className="text-muted-foreground">Plano de formação e certificações</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowBulkModal(true)} 
            className="px-4 py-2 rounded-lg border border-input bg-background hover:bg-muted transition-colors flex items-center gap-2"
            data-testid="bulk-schedule-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Agendar em Massa
          </button>
          <button onClick={() => { resetForm(); setShowModal(true); }} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Nova Formação
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{trainings.filter(t => t.status === 'scheduled').length}</p>
          <p className="text-xs text-muted-foreground">Agendadas</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{trainings.filter(t => t.status === 'in_progress').length}</p>
          <p className="text-xs text-muted-foreground">Em Curso</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{trainings.filter(t => t.status === 'completed').length}</p>
          <p className="text-xs text-muted-foreground">Concluídas</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{trainings.reduce((sum, t) => sum + (t.duration_hours || 0), 0)}h</p>
          <p className="text-xs text-muted-foreground">Horas Total</p>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Estado</label>
            <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="">Todos</option>
              <option value="scheduled">Agendada</option>
              <option value="in_progress">Em Curso</option>
              <option value="completed">Concluída</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo</label>
            <select value={filter.training_type} onChange={(e) => setFilter({ ...filter, training_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="">Todos</option>
              <option value="internal">Interna</option>
              <option value="external">Externa</option>
              <option value="online">Online</option>
              <option value="mandatory">Obrigatória</option>
            </select>
          </div>
        </div>
      </div>

      {/* Trainings Table */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Formação</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Colaborador</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Tipo</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Data</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Duração</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Custo</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Estado</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {trainings.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Sem formações registadas</td></tr>
              ) : (
                trainings.map((training) => (
                  <tr key={training.training_id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{training.title}</p>
                        <p className="text-sm text-muted-foreground">{training.provider}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{getEmployeeName(training.employee_id)}</td>
                    <td className="px-4 py-3 text-sm">{getTypeBadge(training.training_type)}</td>
                    <td className="px-4 py-3 text-sm">{training.start_date}</td>
                    <td className="px-4 py-3 text-sm">{training.duration_hours}h</td>
                    <td className="px-4 py-3 text-sm">{training.cost ? `${training.cost}€` : '-'}</td>
                    <td className="px-4 py-3">{getStatusBadge(training.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(training)}
                          className="p-1.5 rounded hover:bg-muted text-blue-600"
                          title="Editar"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(training.training_id)}
                          className="p-1.5 rounded hover:bg-muted text-red-600"
                          title="Eliminar"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">{editingTraining ? 'Editar Formação' : 'Nova Formação'}</h2>
            </div>
            <form onSubmit={editingTraining ? handleUpdate : handleCreate} className="p-6 space-y-4">
              {/* Tenant selector for super admin */}
              {isSuperAdmin && !editingTraining && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant *</label>
                  <select
                    value={selectedTenant}
                    onChange={(e) => setSelectedTenant(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  >
                    <option value="">Selecione um tenant...</option>
                    {tenants.map(t => (
                      <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Colaborador</label>
                <select value={formData.employee_id} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required>
                  <option value="">Selecionar</option>
                  {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.name}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Título</label><input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo</label>
                  <select value={formData.training_type} onChange={(e) => setFormData({ ...formData, training_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                    <option value="internal">Interna</option>
                    <option value="external">Externa</option>
                    <option value="online">Online</option>
                    <option value="mandatory">Obrigatória</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium mb-1">Entidade</label><input type="text" value={formData.provider} onChange={(e) => setFormData({ ...formData, provider: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Data Início</label><input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
                <div><label className="block text-sm font-medium mb-1">Data Fim</label><input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Duração (h)</label><input type="number" value={formData.duration_hours} onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
                <div><label className="block text-sm font-medium mb-1">Custo (€)</label><input type="number" value={formData.cost} onChange={(e) => setFormData({ ...formData, cost: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              </div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={formData.certification} onChange={(e) => setFormData({ ...formData, certification: e.target.checked })} /><label className="text-sm">Com Certificação</label></div>
              {/* Status field for editing */}
              {editingTraining && (
                <div>
                  <label className="block text-sm font-medium mb-1">Estado</label>
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                    <option value="scheduled">Agendada</option>
                    <option value="in_progress">Em Curso</option>
                    <option value="completed">Concluída</option>
                    <option value="cancelled">Cancelada</option>
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="px-4 py-2 rounded-lg border">Cancelar</button>
                <button type="submit" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground">
                  {editingTraining ? 'Guardar Alterações' : 'Criar Formação'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Schedule Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Agendar Formação em Massa</h2>
              <p className="text-sm text-muted-foreground">Selecione múltiplos colaboradores para a mesma formação</p>
            </div>
            <form onSubmit={handleBulkSchedule} className="p-6 space-y-6">
              {/* Tenant Selection for Super Admin */}
              {isSuperAdmin && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant *</label>
                  <select
                    value={selectedTenant}
                    onChange={(e) => setSelectedTenant(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  >
                    <option value="">Selecione um tenant...</option>
                    {tenants.map(t => (
                      <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Employee Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium">Colaboradores ({selectedEmployees.length} selecionados)</label>
                  <button
                    type="button"
                    onClick={selectAllEmployees}
                    className="text-xs text-primary hover:underline"
                  >
                    {selectedEmployees.length === employees.length ? 'Desselecionar Todos' : 'Selecionar Todos'}
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-border rounded-lg p-3">
                  {employees.map(emp => (
                    <label
                      key={emp.employee_id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        selectedEmployees.includes(emp.employee_id) ? 'bg-primary/10' : 'hover:bg-muted'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(emp.employee_id)}
                        onChange={() => toggleEmployee(emp.employee_id)}
                        className="rounded"
                      />
                      <span className="text-sm truncate">{emp.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Training Details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Título da Formação *</label>
                  <input
                    type="text"
                    value={bulkFormData.title}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, title: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                    placeholder="Ex: Formação em Segurança no Trabalho"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Descrição</label>
                  <textarea
                    value={bulkFormData.description}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, description: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    rows={2}
                    placeholder="Breve descrição da formação"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tipo</label>
                    <select
                      value={bulkFormData.training_type}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, training_type: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    >
                      <option value="internal">Interna</option>
                      <option value="external">Externa</option>
                      <option value="online">Online</option>
                      <option value="mandatory">Obrigatória</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Entidade Formadora</label>
                    <input
                      type="text"
                      value={bulkFormData.provider}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, provider: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Data Início *</label>
                    <input
                      type="date"
                      value={bulkFormData.start_date}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, start_date: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Data Fim</label>
                    <input
                      type="date"
                      value={bulkFormData.end_date}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, end_date: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Duração (h)</label>
                    <input
                      type="number"
                      value={bulkFormData.duration_hours}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, duration_hours: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Custo (€)</label>
                    <input
                      type="number"
                      value={bulkFormData.cost}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, cost: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Local</label>
                    <input
                      type="text"
                      value={bulkFormData.location}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, location: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={bulkFormData.certification}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, certification: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Com Certificação</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={bulkFormData.mandatory}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, mandatory: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Formação Obrigatória</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => { setShowBulkModal(false); setSelectedEmployees([]); }}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={bulkLoading || selectedEmployees.length === 0}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {bulkLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  Agendar para {selectedEmployees.length} Colaboradores
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRTrainingsPage;
